/*
 * StammdatenAaaApp.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-01-26 in huWaWi3 31.17.3
 * Copyright (c) 2023 HUDORA GmbH
 */

import gql from 'graphql-tag'
import { minimatch } from 'minimatch'
import React from 'react'

import { KindLightList } from '../eAP/KindLightList'
import { PERMISSION_FRAGMENT, ROLE_FRAGMENT, USER_FRAGMENT } from '../fragments'
import { KindPage } from '../lib/components/KindPage'

export const getStammdatenSidebarLinks = () => [
  {
    title: 'User',
    path: '/stamm/aaa/user',
    component: (
      <KindPage
        basePath={'/stamm/aaa'}
        componentPath={'user'}
        schemaName={'AaaUser'}
        title={'User'}
        listQuery={USER_LIST_QUERY}
        entityQuery={USER_QUERY}
        editMutation={USER_EDIT_MUTATION}
        rowToolsEnabled={true}
      />
    ),
  },
  { title: 'Rollen', path: '/stamm/aaa/role', component: <AaaRoleApp /> },
  {
    title: 'Rechte GUI',
    path: '/stamm/aaa/permissionGui',
    component: <AaaPermissionFrontendApp />,
  },
  {
    title: 'Rechte Daten',
    path: '/stamm/aaa/permissionData',
    component: <AaaPermissionBackendApp />,
  },
  {
    title: 'DB-Changelog',
    path: '/stamm/aaa/dbaudit',
    component: (
      <KindPage
        basePath={'/stamm/aaa'}
        componentPath={'dbaudit'}
        schemaName={'H3DbAudit'}
        title={'Datenbank-Changelog'}
        listQuery={DBAUDIT_LIST_QUERY}
        entityQuery={DBAUDIT_QUERY}
      />
    ),
  },
  {
    title: 'Access-Audit',
    path: '/stamm/aaa/aaaaudit',
    component: (
      <KindPage
        basePath={'/stamm/aaa'}
        componentPath={'aaaaudit'}
        schemaName={'AaaAccessAudit'}
        title={'Access-Audit'}
        listQuery={AAAAUDIT_LIST_QUERY}
        entityQuery={AAAAUDIT_QUERY}
      />
    ),
  },
  {
    title: 'Deny-Audit',
    path: '/stamm/aaa/denyaudit',
    component: (
      <KindPage
        basePath={'/stamm/aaa'}
        componentPath={'denyaudit'}
        schemaName={'AaaAccessDenied'}
        title={'Access-Denied'}
        listQuery={AAADENIED_LIST_QUERY}
        entityQuery={AAADENIED_QUERY}
      />
    ),
  },
]

export const USER_LIST_QUERY = gql`
  query UserList($cursor: String) {
    nodes: aaaUserPages(after: $cursor, sort: { order: DESC, fields: updated_at }) {
      edges {
        cursor
        node {
          ...userFragment
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${USER_FRAGMENT}
`
export const USER_QUERY = gql`
  query User($nr: ID!) {
    aaaUser(designator: $nr) {
      ...userFragment
    }
  }
  ${USER_FRAGMENT}
`
export const USER_EDIT_MUTATION = gql`
  mutation USerEditMutation($nr: ID!, $input: AaaUserInput!) {
    entity: aaaUserUpdate(designator: $nr, input: $input) {
      ...userFragment
    }
  }
  ${USER_FRAGMENT}
`
const ROLE_LIST_QUERY = gql`
  query RoleList($cursor: String) {
    nodes: aaaRolePages(after: $cursor, sort: { fields: updated_at, order: DESC }) {
      edges {
        cursor
        node {
          ...roleFragment
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${ROLE_FRAGMENT}
`
const ROLE_QUERY = gql`
  query Role($nr: ID!) {
    entity: aaaRole(designator: $nr) {
      ...roleFragment
      userList {
        ...userFragment
      }
      permissionList {
        ...permissionFragment
      }
    }
  }
  ${ROLE_FRAGMENT}
  ${PERMISSION_FRAGMENT}
  ${USER_FRAGMENT}
`

export const ROLE_CREATE_MUTATION = gql`
  mutation RoleCreateMutation($input: AaaRoleInput!) {
    entity: aaaRoleCreate(input: $input) {
      ...roleFragment
    }
  }
  ${ROLE_FRAGMENT}
`
export const ROLE_EDIT_MUTATION = gql`
  mutation RoleEditMutation($designator: ID!, $input: AaaRoleInput!) {
    entity: aaaRoleUpdate(designator: $designator, input: $input) {
      ...roleFragment
    }
  }
  ${ROLE_FRAGMENT}
`
export const AaaRoleApp = () => {
  // Rendert entweder die Startseite (Suche) oder einem Lieferanten
  return (
    <KindPage
      basePath={'/stamm/aaa'}
      componentPath={'role'}
      schemaName={'AaaRole'}
      title={'Rollen'}
      listQuery={ROLE_LIST_QUERY}
      entityQuery={ROLE_QUERY}
      createMutation={ROLE_CREATE_MUTATION}
      editMutation={ROLE_EDIT_MUTATION}
    >
      <RoleDetails entity={null} />
    </KindPage>
  )
}
function RoleDetails(props) {
  return (
    <>
      <KindLightList
        entities={props?.entity?.permissionList}
        schemaName="AaaPermission"
        hideExcept={[
          'designator',
          'name',
          'type',
          'operation',
          'concern',
          'subconcern',
          'returnType',
          'created_at',
          'updated_at',
        ]}
      />
      <KindLightList entities={props?.entity?.userList} schemaName="AaaUser" hideExcept={null} />
    </>
  )
}
export const AaaPermissionFrontendApp = () => (
  <KindPage
    basePath={'/stamm/aaa'}
    componentPath={'permissionGui'}
    schemaName={'AaaPermission'}
    title={'Berechtigungen'}
    listQuery={PERMISSION_FRONTEND_LIST_QUERY}
    entityQuery={PERMISSION_QUERY}
    createMutation={PERMISSION_CREATE_MUTATION}
    editMutation={PERMISSION_UPDATE_MUTATION}
    hideExcept={[
      'designator',
      'name',
      'operation',
      'concern',
      'subconcern',
      'fieldName',
      'ist_aktiv',
      'updated_at',
      'created_at',
    ]}
  >
    <PermissionDetails entity={null} />
  </KindPage>
)
export const AaaPermissionBackendApp = () => (
  <KindPage
    basePath={'/stamm/aaa'}
    componentPath={'permissionData'}
    schemaName={'AaaPermission'}
    title={'Berechtigungen'}
    listQuery={PERMISSION_BACKEND_LIST_QUERY}
    entityQuery={PERMISSION_QUERY}
    createMutation={PERMISSION_CREATE_MUTATION}
    editMutation={PERMISSION_UPDATE_MUTATION}
    hideExcept={[
      'designator',
      'name',
      'type',
      'operation',
      'concern',
      'subconcern',
      'returnType',
      'fieldName',
      'resolverName',
      'ist_aktiv',
      'updated_at',
      'created_at',
    ]}
  >
    <PermissionDetails entity={null} />
  </KindPage>
)
function PermissionDetails(props) {
  return (
    <>
      <MatchPlayground permission={props?.entity} />
      <KindLightList entities={props?.entity?.roles} schemaName="AaaRole" hideExcept={null} />
    </>
  )
}

function MatchPlayground(props: { permission: Record<string, any> }) {
  const path = location
    .split('/')
    .map((x) => (x.match(/\d/) ? '#' : x))
    .join('/')
  const pathExamples = [path, `${path}/foo`, `${path}/bar/`, `${path}/foo/bar`, '/', '/doc/', location]
  if (props.permission?.type !== 'gui') {
    return null
  }
  return (
    <div>
      <h3>Test</h3>
      <div>
        Hier kann man Pfade <em>ähnlich</em> wie auf dem Server testen:
        <table>
          <tbody>
            {pathExamples.map((example) => (
              <tr key={example}>
                <td>{example}</td>
                <td>{props.permission?.subconcern}</td>
                <td>
                  {minimatch(example, props.permission?.subconcern || '/', { dot: true }) ? '✅' : '❌'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const PERMISSION_FRONTEND_LIST_QUERY = gql`
  query PermissionListFrondendQuery($cursor: String) {
    nodes: aaaPermissionPages(
      after: $cursor
      filter: { type: { eq: "gui" } }
      sort: { order: DESC, fields: updated_at }
    ) {
      edges {
        node {
          ...permissionFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${PERMISSION_FRAGMENT}
`
const PERMISSION_BACKEND_LIST_QUERY = gql`
  query PermissionListBackendQuery($cursor: String) {
    nodes: aaaPermissionPages(
      after: $cursor
      filter: { type: { eq: "backend" } }
      sort: { order: DESC, fields: updated_at }
    ) {
      edges {
        node {
          ...permissionFragment
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${PERMISSION_FRAGMENT}
`
const PERMISSION_QUERY = gql`
  query Permission($nr: ID!) {
    entity: aaaPermission(designator: $nr) {
      ...permissionFragment
      roles {
        ...roleFragment
      }
    }
  }
  ${PERMISSION_FRAGMENT}
  ${ROLE_FRAGMENT}
`

export const PERMISSION_CREATE_MUTATION = gql`
  mutation PermissionCreateMutation($input: AaaPermissionInput!) {
    entity: aaaPermissionCreate(input: $input) {
      ...permissionFragment
    }
  }
  ${PERMISSION_FRAGMENT}
`
export const PERMISSION_UPDATE_MUTATION = gql`
  mutation PermussionUpdateMutation($designator: ID!, $input: AaaPermissionInput!) {
    entity: aaaPermissionUpdate(designator: $designator, input: $input) {
      ...permissionFragment
    }
  }
  ${PERMISSION_FRAGMENT}
`

export const DBAUDIT_LIST_QUERY = gql`
  query DbAuditList($cursor: String) {
    nodes: h3DbAuditPages(after: $cursor) {
      edges {
        node {
          id
          designator
          created_at
          forDesignator
          typ
          kind
          uid
          user
          text
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`
export const DBAUDIT_QUERY = gql`
  query Audit($nr: ID!) {
    entity: h3DbAudit(designator: $nr) {
      id
      designator
      created_at
      forDesignator
      typ
      kind
      uid
      user
      text
      new
      old
    }
  }
`
export const AAAAUDIT_LIST_QUERY = gql`
  query AaaAuditList($cursor: String) {
    nodes: aaaAccessAuditPages(after: $cursor, sort: { fields: created_at, order: DESC }) {
      edges {
        node {
          designator
          id
          concern
          client
          created_at
          environment
          fieldName
          fileName
          name
          project
          operation
          requestId
          resolverName
          returnType
          subconcern
          type
          uid
          user
          version
          updated_at
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`
export const AAAAUDIT_QUERY = gql`
  query AaaAudit($nr: ID!) {
    entity: aaaAccessAudit(designator: $nr) {
      designator
      id
      concern
      client
      created_at
      environment
      fieldName
      fileName
      name
      project
      operation
      requestId
      resolverName
      returnType
      subconcern
      type
      uid
      user
      version
      updated_at
      destinationPermissions {
        designator
        name
      }
      destinationRoles {
        designator
        name
        userzahl
      }
    }
  }
`
export const AAADENIED_LIST_QUERY = gql`
  query AaaDenyList($cursor: String) {
    nodes: aaaAccessDeniedPages(after: $cursor, sort: { fields: created_at, order: DESC }) {
      edges {
        node {
          designator
          client
          concern
          created_at
          _keyStr
          environment
          fieldName
          fileName
          id
          name
          operation
          project
          requestId
          resolverName
          returnType
          subconcern
          type
          uid
          updated_at
          user
          version
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`
export const AAADENIED_QUERY = gql`
  query AaaDeny($nr: ID!) {
    entity: aaaAccessDenied(designator: $nr) {
      designator
      client
      concern
      created_at
      _keyStr
      environment
      fieldName
      fileName
      id
      name
      operation
      project
      requestId
      resolverName
      returnType
      subconcern
      type
      uid
      updated_at
      user
      version
      suggestedRoles {
        designator
        name
        userzahl
      }
    }
  }
`
